import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { Article } from "@/mocks/articles";

interface HeroCarouselProps {
  items: Article[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const timer = useRef<number | null>(null);
  const touchStart = useRef<number>(0);

  const goTo = (next: number) => {
    setIndex((next + items.length) % items.length);
  };

  useEffect(() => {
    if (items.length <= 1) return undefined;
    timer.current = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, 6000);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [items.length]);

  if (items.length === 0) return null;

  const current = items[index];

  const stopAuto = () => {
    if (timer.current) window.clearInterval(timer.current);
  };

  return (
    <section className="relative w-full h-[520px] md:h-[640px] overflow-hidden bg-primary-950">
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={item.cover}
            alt={item.title}
            title={`${item.title} - 黑人文化资讯`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/95 via-primary-950/70 to-primary-950/40" />
        </div>
      ))}

      <div className="relative z-10 h-full w-full flex items-center px-4 md:px-8">
        <div className="mx-auto max-w-content w-full">
          <div className="max-w-2xl" key={current.id}>
            <div className="flex items-center gap-3 mb-5 animate-fade-up">
              <span className="text-xs px-3 py-1 rounded-full bg-accent-500 text-secondary-950 font-medium whitespace-nowrap">
                {current.categoryName}
              </span>
              <span className="text-xs px-3 py-1 rounded-full bg-background-50/15 text-background-100 backdrop-blur whitespace-nowrap">
                焦点推荐
              </span>
            </div>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 leading-tight animate-fade-up">
              {current.title}
            </h1>
            <p className="mt-5 text-sm md:text-base text-background-200 leading-relaxed line-clamp-3 animate-fade-up">
              {current.summary}
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-4 animate-fade-up">
              <Link
                to={`/detail/${current.id}`}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-accent-500 text-secondary-950 text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                阅读全文
                <i className="ri-arrow-right-line" />
              </Link>
              <Link
                to={`/category/${current.category}`}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-background-50/40 text-background-50 text-sm font-medium hover:bg-background-50/10 transition-colors cursor-pointer whitespace-nowrap"
              >
                浏览{current.categoryName}
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-0 right-0 z-20 px-4 md:px-8">
        <div className="mx-auto max-w-content flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            {items.map((item, i) => (
              <button
                key={item.id}
                type="button"
                aria-label={`切换到第 ${i + 1} 张`}
                onClick={() => {
                  stopAuto();
                  setIndex(i);
                }}
                className={`h-1.5 rounded-full transition-all cursor-pointer ${
                  i === index ? "w-8 bg-accent-500" : "w-3 bg-background-50/40 hover:bg-background-50/70"
                }`}
              />
            ))}
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <button
              type="button"
              aria-label="上一张"
              onClick={() => {
                stopAuto();
                goTo(index - 1);
              }}
              className="w-10 h-10 flex items-center justify-center rounded-full border border-background-50/30 text-background-50 hover:bg-background-50/10 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line" />
            </button>
            <button
              type="button"
              aria-label="下一张"
              onClick={() => {
                stopAuto();
                goTo(index + 1);
              }}
              className="w-10 h-10 flex items-center justify-center rounded-full border border-background-50/30 text-background-50 hover:bg-background-50/10 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-line" />
            </button>
          </div>
        </div>
      </div>

      <div
        className="absolute inset-0 z-0"
        onTouchStart={(e) => {
          touchStart.current = e.changedTouches[0].clientX;
        }}
        onTouchEnd={(e) => {
          const delta = e.changedTouches[0].clientX - touchStart.current;
          if (Math.abs(delta) > 50) {
            stopAuto();
            goTo(delta > 0 ? index - 1 : index + 1);
          }
        }}
      />
    </section>
  );
}